import { useMemo } from 'react';
import { createTheme } from '@material-ui/core/styles';
import Color, { ColorInstance } from 'color';
import { Theme, FormThemeValues } from 'assets/types';

const defaultMuiTheme = createTheme();

export const useCustomTheme = (
  formTheme: FormThemeValues,
  baseTheme: Theme
) => {
  return useMemo(() => {
    // Return base theme if no custom colors
    if (
      !formTheme.primaryColor &&
      !formTheme.secondaryColor &&
      !formTheme.backgroundColor
    ) {
      return baseTheme;
    }

    return createCustomTheme(formTheme, baseTheme);
  }, [formTheme, baseTheme]);
};

// Extract theme creation logic to separate function
const createCustomTheme = (theme: FormThemeValues, baseTheme: Theme) => {
  const bgColor = Color(
    theme.backgroundColor || baseTheme.palette.background.paper
  );
  const primary = Color(theme.primaryColor || baseTheme.palette.primary.main);
  const secondary = Color(
    theme.secondaryColor || baseTheme.palette.secondary.main
  );

  const isDark = bgColor.isDark();

  return createTheme({
    ...baseTheme,
    palette: {
      ...(isDark ? baseTheme.palette : defaultMuiTheme.palette),
      primary: createColorPalette(primary),
      secondary: createColorPalette(secondary),
      background: createBackgroundPalette(
        bgColor,
        theme.backgroundGradientEnabled,
        primary,
        secondary
      ),
    },
  });
};

// Helper functions
const createColorPalette = (color: ColorInstance) => ({
  main: color.hex(),
  light: color.lighten(0.2).hex(),
  dark: color.darken(0.2).hex(),
  contrastText: color.isLight() ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
});

const createBackgroundPalette = (
  bgColor: ColorInstance,
  gradientEnabled: boolean | undefined,
  primary: ColorInstance,
  secondary: ColorInstance
) => {
  const isDark = bgColor.isDark();
  const paperColor = bgColor.hex();
  const darkPaperColor = isDark
    ? bgColor.gray(17.5).hex()
    : bgColor.gray(95).hex();

  return {
    default: paperColor,
    paper: paperColor,
    lightPaper: isDark ? bgColor.gray(35).hex() : bgColor.gray(80).hex(),
    darkPaper: darkPaperColor,
    gradient: gradientEnabled
      ? `linear-gradient(315deg, ${secondary.hex()} 0%, ${primary.hex()} 100%)`
      : darkPaperColor,
  };
};
